import { useState, useEffect, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Dashboard from "@/pages/Dashboard";
import ProposalBuilder from "@/pages/ProposalBuilder";
import ProposalsList from "@/pages/ProposalsList";
import Settings from "@/pages/Settings";
import Integrations from "@/pages/Integrations";
import IntegrationCallback from "@/pages/IntegrationCallback";
import ProspectView from "@/pages/ProspectView";
import PaymentSuccess from "@/pages/PaymentSuccess";
import FieldMapper from "@/pages/FieldMapper";
import GoogleAuthCallback from "@/pages/GoogleAuthCallback";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";

// Auto-detect backend URL based on current domain
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  
  // Production domain
  if (hostname === 'prospectconnect.co' || hostname === 'www.prospectconnect.co') {
    return 'https://prospectconnect.co';
  }
  
  // Preview/development - use environment variable or derive from current URL
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Fallback: use current origin
  return window.location.origin;
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export { API, BACKEND_URL };

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Simple data cache for reducing API calls between page navigations
  const dataCache = useRef({
    proposals: null,
    proposalsTimestamp: 0,
    stats: null,
    statsTimestamp: 0,
    integrations: null,
    integrationsTimestamp: 0
  });
  
  const CACHE_TTL = 30000; // 30 seconds cache

  const checkAuth = async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      setToken(null);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // ignore
    }
    localStorage.removeItem('token');
    sessionStorage.removeItem('welcomeToastShown');
    setToken(null);
    setUser(null);
    // Clear cache on logout
    dataCache.current = {
      proposals: null,
      proposalsTimestamp: 0,
      stats: null,
      statsTimestamp: 0,
      integrations: null,
      integrationsTimestamp: 0
    };
  };

  const updateUser = (userData) => {
    setUser(userData);
  };
  
  // Cache helpers for pages to use
  const getCachedData = (key) => {
    const cache = dataCache.current;
    if (cache[key] && Date.now() - cache[`${key}Timestamp`] < CACHE_TTL) {
      return cache[key];
    }
    return null;
  };
  
  const setCachedData = (key, data) => {
    dataCache.current[key] = data;
    dataCache.current[`${key}Timestamp`] = Date.now();
  };
  
  const invalidateCache = (key) => {
    if (key) {
      dataCache.current[key] = null;
      dataCache.current[`${key}Timestamp`] = 0;
    } else {
      // Invalidate all
      dataCache.current = {
        proposals: null,
        proposalsTimestamp: 0,
        stats: null,
        statsTimestamp: 0,
        integrations: null,
        integrationsTimestamp: 0
      };
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, loading, token, login, register, logout, updateUser, checkAuth,
      getCachedData, setCachedData, invalidateCache,
      mobileMenuOpen, setMobileMenuOpen
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="animate-pulse text-forest font-display text-2xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Auth Callback for Google OAuth
const AuthCallback = () => {
  const navigate = useNavigate();
  const { updateUser, checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];

      if (sessionId) {
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
          updateUser(response.data);
          
          // Check if user needs to connect Gmail for sending
          if (response.data.needs_gmail_connect) {
            // Navigate to dashboard with a flag to show Gmail connect prompt
            navigate('/dashboard?connect_gmail=true', { replace: true });
          } else {
            navigate('/dashboard', { replace: true });
          }
        } catch (error) {
          console.error('Auth callback error:', error);
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processSession();
  }, [navigate, updateUser]);

  return (
    <div className="min-h-screen bg-sand flex items-center justify-center">
      <div className="animate-pulse text-forest font-display text-2xl">Signing you in...</div>
    </div>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id - must be synchronous during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/proposals" element={<ProtectedRoute><ProposalsList /></ProtectedRoute>} />
      <Route path="/proposals/new" element={<ProtectedRoute><ProposalBuilder /></ProtectedRoute>} />
      <Route path="/proposals/:id" element={<ProtectedRoute><ProposalBuilder /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/documents/:docId/map" element={<ProtectedRoute><FieldMapper /></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
      <Route path="/integrations/callback/:provider" element={<ProtectedRoute><IntegrationCallback /></ProtectedRoute>} />
      <Route path="/view/:id" element={<ProspectView />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="bottom-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
